import React, { useMemo } from "react";
import { entities } from "@/api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  Download, 
  ClipboardCheck, 
  AlertCircle,
  Upload,
  FolderKanban,
  FileSpreadsheet,
  AlertTriangle,
  XCircle,
  Calendar,
  ArrowRight,
  Package,
  Lightbulb,
  ListChecks,
  User
} from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl, safeParseISO } from "@/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { differenceInDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";

/** Coluna padrão do Kanban para etapa Pregão (alinhado a StatusReport / Licitacao). */
const KANBAN_COLUNA_PREGAO = "pregao";
/** Ações com entrega nos próximos N dias (inclusive hoje) ou já vencidas. */
const DIAS_PROXIMO_VENCIMENTO_ACAO = 7;

export default function Dashboard() {
  const { data: editais = [], isLoading: loadingEditais, error } = useQuery({
    queryKey: ['editais-dashboard'],
    queryFn: async () => {
      try {
        const data = await entities.Edital.list("-updatedAt", 200);
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Erro ao carregar editais:", err);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000
  });

  const { data: documentos = [], isLoading: loadingDocumentos } = useQuery({
    queryKey: ['documentos-dashboard'],
    queryFn: async () => {
      try {
        const data = await entities.Documento.list();
        return (Array.isArray(data) ? data : []).slice(0, 50);
      } catch (err) {
        console.error("Erro ao carregar documentos:", err);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000
  });

  const { data: propostas = [], isLoading: loadingPropostas } = useQuery({
    queryKey: ['propostas-dashboard'],
    queryFn: async () => {
      try {
        const data = await entities.Proposta.list("-createdAt", 50);
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Erro ao carregar propostas:", err);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000
  });

  const { data: atestados = [], isLoading: loadingAtestados } = useQuery({
    queryKey: ['atestados-dashboard'],
    queryFn: async () => [],
    retry: 2,
    staleTime: 30000
  });

  const { data: licoesAprendidas = [], isLoading: loadingLicoes } = useQuery({
    queryKey: ['licoes-aprendidas-dashboard'],
    queryFn: async () => {
      try {
        const data = await entities.LicaoAprendida.list('-createdAt');
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Erro ao carregar lições aprendidas:", err);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000
  });

  // Garantir arrays (evita C.filter is not a function quando API/cache retorna objeto)
  const safeEditais = Array.isArray(editais) ? editais : [];
  const safeDocumentos = Array.isArray(documentos) ? documentos : [];
  const safePropostas = Array.isArray(propostas) ? propostas : [];
  const safeAtestados = Array.isArray(atestados) ? atestados : [];
  const safeLicoes = Array.isArray(licoesAprendidas) ? licoesAprendidas : [];

  /** Editais na coluna Pregão com ações vencidas ou com entrega em até N dias (não concluídas). */
  const acoesPregaoUrgentesPorEdital = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const grupos = [];

    for (const e of safeEditais) {
      if (e.oculto_kanban === true || e.ocultoKanban === true) continue;
      const col = e.coluna_kanban ?? e.colunaKanban;
      if (col !== KANBAN_COLUNA_PREGAO) continue;

      const listaAcoes = Array.isArray(e.acoes) ? e.acoes : [];
      const itens = [];

      for (const acao of listaAcoes) {
        const pct = acao.percentual_conclusao ?? acao.percentualConclusao;
        if (pct === 100 || pct === "100") continue;

        const dataStr = acao.data_entrega ?? acao.dataEntrega;
        if (dataStr == null || dataStr === "" || dataStr === "Não localizado") continue;

        const dataEntrega = safeParseISO(String(dataStr));
        if (!dataEntrega) continue;
        dataEntrega.setHours(0, 0, 0, 0);
        const diasRestantes = differenceInDays(dataEntrega, hoje);

        if (
          diasRestantes < 0 ||
          (diasRestantes >= 0 && diasRestantes <= DIAS_PROXIMO_VENCIMENTO_ACAO)
        ) {
          itens.push({ acao, diasRestantes, dataEntrega });
        }
      }

      if (itens.length === 0) continue;

      itens.sort((a, b) => a.diasRestantes - b.diasRestantes);
      const tituloExibicao =
        e.titulo_licitacao ||
        e.identificacao ||
        e.instituicao ||
        e.nome_arquivo ||
        "Edital";
      const numeroPregao = e.numero_pregao ?? e.numeroPregao ?? null;

      grupos.push({
        editalId: e.id,
        tituloExibicao,
        numeroPregao,
        itens,
      });
    }

    grupos.sort((a, b) => {
      const minA = Math.min(...a.itens.map((i) => i.diasRestantes));
      const minB = Math.min(...b.itens.map((i) => i.diasRestantes));
      return minA - minB;
    });

    return grupos;
  }, [safeEditais]);

  // Estatísticas de Editais
  const totalEditais = safeEditais.length;
  const editalsConcluidos = safeEditais.filter(e => e.status === "concluido").length;
  const editaisProcessando = safeEditais.filter(e => e.status === "processando").length;
  const confiancaMedia = safeEditais.filter(e => e.confianca_media).reduce((acc, e) => acc + (e.confianca_media || 0), 0) / (safeEditais.filter(e => e.confianca_media).length || 1);
  const statusAprovacao = (e) => e.statusAprovacao ?? e.status_aprovacao;
  const pendentesAprovacao = safeEditais.filter(e => e.status === "concluido" && (statusAprovacao(e) === "pendente" || !statusAprovacao(e))).length;
  const editaisAprovados = safeEditais.filter(e => statusAprovacao(e) === "aprovar_seguir").length;

  // Estatísticas de Documentos
  const totalDocumentos = safeDocumentos.length;
  const documentosValidos = safeDocumentos.filter(d => (d.status || d.status) === "valido").length;
  const documentosVencendo = safeDocumentos.filter(d => d.status === "proximo_vencimento").length;
  const documentosVencidos = safeDocumentos.filter(d => d.status === "vencido").length;

  // Estatísticas de Propostas
  const totalPropostas = safePropostas.length;
  const propostasFinalizadas = safePropostas.filter(p => p.status === "finalizada").length;
  const propostasEnviadas = safePropostas.filter(p => p.status === "enviada").length;
  const propostasRascunho = safePropostas.filter(p => p.status === "rascunho").length;

  // Estatísticas de Atestados
  const totalAtestados = safeAtestados.length;
  const atestadosFinalizados = safeAtestados.filter(a => a.status === "finalizado").length;
  const atestadosRascunho = safeAtestados.filter(a => a.status === "rascunho").length;

  // Estatísticas de Lições Aprendidas
  const totalLicoes = safeLicoes.length;
  const licoesVitorias = safeLicoes.filter(l => l.classificacao === "1º").length;

  // Editais em Licitação (no Kanban)
  const editaisEmLicitacao = safeEditais.filter(e => e.coluna_kanban && e.coluna_kanban !== "encerrado").length;

  // Módulos do Sistema (useMemo evita TDZ com ícones minificados em produção)
  const modulos = useMemo(() => [
    {
      titulo: "Processar Edital",
      descricao: "Upload e extração de dados",
      icone: Upload,
      cor: "bg-blue-500",
      link: "Upload",
      valor: editaisProcessando,
      label: "processando"
    },
    {
      titulo: "Análise e Aprovação",
      descricao: "Revisar editais processados",
      icone: ClipboardCheck,
      cor: "bg-amber-500",
      link: "Analise",
      valor: pendentesAprovacao,
      label: "pendentes",
      destaque: pendentesAprovacao > 0
    },
    {
      titulo: "Licitação",
      descricao: "Gestão do processo licitatório",
      icone: FolderKanban,
      cor: "bg-purple-500",
      link: "Licitacao",
      valor: editaisEmLicitacao,
      label: "em andamento"
    },
    {
      titulo: "Propostas",
      descricao: "Criar e gerenciar propostas",
      icone: FileSpreadsheet,
      cor: "bg-green-500",
      link: "Propostas",
      valor: totalPropostas,
      label: "propostas"
    },
    {
      titulo: "Documentos",
      descricao: "Certidões e documentos",
      icone: FileText,
      cor: "bg-indigo-500",
      link: documentosVencidos > 0 ? "Documentos?status=vencido" : 
            documentosVencendo > 0 ? "Documentos?status=proximo_vencimento" : 
            "Documentos",
      valor: totalDocumentos,
      label: "documentos",
      alerta: documentosVencendo + documentosVencidos,
      alertaTexto: documentosVencendo + documentosVencidos > 0 ? `${documentosVencendo + documentosVencidos} vencendo/vencidos` : null
    },
    {
      titulo: "Histórico",
      descricao: "Todos os editais processados",
      icone: Package,
      cor: "bg-slate-500",
      link: "Historico",
      valor: totalEditais,
      label: "editais"
    },
    {
      titulo: "Lições Aprendidas",
      descricao: "Registro de experiências de licitações",
      icone: Lightbulb,
      cor: "bg-amber-500",
      link: "LearnedLessons",
      valor: totalLicoes,
      label: "lições"
    }
  ], [editaisProcessando, pendentesAprovacao, editaisEmLicitacao, totalPropostas, documentosVencidos, documentosVencendo, totalDocumentos, totalEditais, totalLicoes]);

  // Alertas Importantes
  const alertas = [];
  
  if (pendentesAprovacao > 0) {
    alertas.push({
      tipo: "warning",
      mensagem: `${pendentesAprovacao} edital(is) aguardando análise e aprovação`,
      link: "Analise",
      icone: ClipboardCheck
    });
  }

  if (documentosVencidos > 0) {
    alertas.push({
      tipo: "error",
      mensagem: `${documentosVencidos} documento(s) vencido(s) - Renovação urgente necessária`,
      link: "Documentos?status=vencido",
      icone: XCircle
    });
  }

  if (documentosVencendo > 0) {
    alertas.push({
      tipo: "warning",
      mensagem: `${documentosVencendo} documento(s) próximo(s) do vencimento`,
      link: "Documentos?status=proximo_vencimento",
      icone: AlertTriangle
    });
  }

  if (editaisProcessando > 0) {
    alertas.push({
      tipo: "info",
      mensagem: `${editaisProcessando} edital(is) sendo processado(s)`,
      link: "Historico",
      icone: Clock
    });
  }

  // Atividades Recentes
  const atividadesRecentes = [];
  
  // Últimos editais aprovados
  safeEditais
    .filter(e => e.status_aprovacao === "aprovar_seguir")
    .slice(0, 3)
    .forEach(e => {
      atividadesRecentes.push({
        tipo: "edital_aprovado",
        titulo: e.titulo_licitacao || e.instituicao || e.nome_arquivo,
        data: e.data_aprovacao || e.created_date,
        link: `Resultados?id=${e.id}`,
        icone: CheckCircle2,
        cor: "text-green-600"
      });
    });

  // Últimas propostas criadas
  safePropostas
    .slice(0, 2)
    .forEach(p => {
      atividadesRecentes.push({
        tipo: "proposta",
        titulo: `Proposta ${p.numero_proposta || (p.id ? String(p.id).slice(0, 8) : '')}`,
        data: p.created_date || p.created_at,
        link: `VisualizarProposta?id=${p.id}`,
        icone: FileSpreadsheet,
        cor: "text-blue-600"
      });
    });

  // Últimas lições aprendidas
  safeLicoes
    .slice(0, 2)
    .forEach(l => {
      atividadesRecentes.push({
        tipo: "licao_aprendida",
        titulo: l.edital_nome || l.instituicao || `Lição ${l.id ? String(l.id).slice(0, 8) : ''}`,
        data: l.created_at,
        link: "LearnedLessons",
        icone: Lightbulb,
        cor: "text-amber-600"
      });
    });

  // Ordenar por data
  atividadesRecentes.sort((a, b) => new Date(b.data) - new Date(a.data));
  const atividadesLimitadas = atividadesRecentes.slice(0, 5);

  if (error) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Erro ao carregar dados. Por favor, recarregue a página ou entre em contato com o suporte.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const isLoading = loadingEditais || loadingDocumentos || loadingPropostas || loadingAtestados || loadingLicoes;

  return (
    <div className="p-4 md:p-8 min-h-screen" data-tour="tour-Dashboard-root">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Dashboard</h1>
          <p className="text-slate-600">Visão geral do sistema de gestão de licitações</p>
        </div>

        {/* Alertas Importantes */}
        {alertas.length > 0 && (
          <div className="grid gap-3 mb-6">
            {alertas.map((alerta, idx) => {
              const Icon = alerta.icone;
              const bgColor = alerta.tipo === "error" ? "bg-red-50 border-red-200" : 
                               alerta.tipo === "warning" ? "bg-amber-50 border-amber-200" : 
                               "bg-blue-50 border-blue-200";
              const textColor = alerta.tipo === "error" ? "text-red-900" : 
                                 alerta.tipo === "warning" ? "text-amber-900" : 
                                 "text-blue-900";
              const iconColor = alerta.tipo === "error" ? "text-red-600" : 
                                 alerta.tipo === "warning" ? "text-amber-600" : 
                                 "text-blue-600";
              
              return (
                <Link key={idx} to={createPageUrl(alerta.link)}>
                  <Alert className={`${bgColor} border cursor-pointer hover:shadow-md transition-shadow`}>
                    <Icon className={`h-4 w-4 ${iconColor}`} />
                    <AlertDescription className={textColor}>
                      <div className="flex items-center justify-between">
                        <span>{alerta.mensagem}</span>
                        <ArrowRight className={`w-4 h-4 ${iconColor}`} />
                      </div>
                    </AlertDescription>
                  </Alert>
                </Link>
              );
            })}
          </div>
        )}

        {/* Estatísticas Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-tour="tour-Dashboard-primary">
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <FileText className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 text-white border-white/30">
                  {editalsConcluidos}/{totalEditais}
                </Badge>
              </div>
              <p className="text-sm opacity-90 mb-1">Total de Editais</p>
              <p className="text-3xl font-bold">{totalEditais}</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 text-white border-white/30">
                  {editaisAprovados} aprovados
                </Badge>
              </div>
              <p className="text-sm opacity-90 mb-1">Concluídos</p>
              <p className="text-3xl font-bold">{editalsConcluidos}</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 text-white border-white/30">
                  {propostasFinalizadas} finalizadas
                </Badge>
              </div>
              <p className="text-sm opacity-90 mb-1">Propostas</p>
              <p className="text-3xl font-bold">{totalPropostas}</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <FileText className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 text-white border-white/30">
                  {documentosValidos} válidos
                </Badge>
              </div>
              <p className="text-sm opacity-90 mb-1">Documentos</p>
              <p className="text-3xl font-bold">{totalDocumentos}</p>
            </CardContent>
          </Card>
        </div>

        {/* Módulos do Sistema */}
        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-8">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl">Módulos do Sistema</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modulos.map((modulo, idx) => {
                const Icon = modulo.icone;
                return (
                  <Link key={idx} to={createPageUrl(modulo.link)}>
                    <Card className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${modulo.destaque ? 'ring-2 ring-amber-400 bg-amber-50' : ''}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`${modulo.cor} p-3 rounded-xl text-white flex-shrink-0`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 mb-1">{modulo.titulo}</h3>
                            <p className="text-sm text-slate-600 mb-3">{modulo.descricao}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-slate-900">{modulo.valor}</span>
                                <span className="text-sm text-slate-600">{modulo.label}</span>
                              </div>
                              {modulo.alerta > 0 && modulo.alertaTexto && (
                                <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50 text-xs">
                                  {modulo.alertaTexto}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Atividades Recentes e Resumo Rápido (duas colunas); Ações no Pregão vem abaixo, largura total */}
        <div className="grid lg:grid-cols-2 gap-6 w-full min-w-0">
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-xl">Atividades Recentes</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {atividadesLimitadas.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nenhuma atividade recente</p>
              ) : (
                <div className="space-y-4">
                  {atividadesLimitadas.map((atividade, idx) => {
                    const Icon = atividade.icone;
                    return (
                      <Link key={idx} to={createPageUrl(atividade.link)}>
                        <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                          <div className={`p-2 rounded-lg bg-slate-100 ${atividade.cor}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{atividade.titulo}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(atividade.data).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Resumo Rápido */}
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-xl">Resumo Rápido</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FolderKanban className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Em Licitação</p>
                      <p className="text-lg font-bold text-slate-900">{editaisEmLicitacao}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Propostas Enviadas</p>
                      <p className="text-lg font-bold text-slate-900">{propostasEnviadas}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <FileText className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Atestados Criados</p>
                      <p className="text-lg font-bold text-slate-900">{totalAtestados}</p>
                    </div>
                  </div>
                </div>

                <Link to={createPageUrl("LearnedLessons")}>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Lightbulb className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Lições Aprendidas</p>
                        <p className="text-lg font-bold text-slate-900">{totalLicoes}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-amber-600" />
                  </div>
                </Link>

                {documentosVencidos > 0 && (
                  <Link to={createPageUrl("Documentos?status=vencido")}>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <XCircle className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Docs. Vencidos</p>
                          <p className="text-lg font-bold text-red-900">{documentosVencidos}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-red-600" />
                    </div>
                  </Link>
                )}
                
                {documentosVencendo > 0 && (
                  <Link to={createPageUrl("Documentos?status=proximo_vencimento")}>
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Docs. Próx. Vencimento</p>
                          <p className="text-lg font-bold text-amber-900">{documentosVencendo}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-amber-600" />
                    </div>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ações no Pregão: linha completa abaixo das duas colunas */}
        {acoesPregaoUrgentesPorEdital.length > 0 && (
          <Card className="w-full min-w-0 border border-amber-200 shadow-md bg-amber-50/40 backdrop-blur-sm mb-8">
            <CardHeader className="border-b border-amber-100 pb-4">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-950">
                <ListChecks className="w-5 h-5 text-amber-700 shrink-0" />
                Ações no Pregão (vencidas ou próximas do vencimento)
              </CardTitle>
              <p className="text-sm text-amber-900/80 font-normal mt-1">
                Por pregão: número do pregão e título do edital. Até {DIAS_PROXIMO_VENCIMENTO_ACAO}{" "}
                dias para a data de entrega, ou já vencidas.
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-5">
              {acoesPregaoUrgentesPorEdital.map((grupo) => (
                <div
                  key={grupo.editalId}
                  className="rounded-lg border border-amber-200/80 bg-white/90 p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                        Pregão
                        {grupo.numeroPregao != null && String(grupo.numeroPregao).trim() !== ""
                          ? `: ${String(grupo.numeroPregao).trim()}`
                          : ""}
                      </p>
                      <p className="text-sm font-medium text-slate-900 break-words">
                        {grupo.tituloExibicao}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" className="shrink-0 border-amber-300" asChild>
                      <Link
                        to={`${createPageUrl("Resultados")}?id=${encodeURIComponent(grupo.editalId)}&tab=acoes`}
                      >
                        Abrir ações
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                  <ul className="space-y-2">
                    {grupo.itens.map(({ acao, diasRestantes, dataEntrega }, idx) => {
                      const desc =
                        (acao.descricao && String(acao.descricao).trim()) ||
                        (acao.objetivo && String(acao.objetivo).trim()) ||
                        "Ação sem descrição";
                      const nomeResp = String(
                        acao.responsavel ?? acao.responsavelNome ?? ""
                      ).trim();
                      const emailResp = String(
                        acao.responsavel_email ?? acao.responsavelEmail ?? ""
                      ).trim();
                      const textoResponsavel =
                        nomeResp && emailResp && emailResp.toLowerCase() !== nomeResp.toLowerCase()
                          ? `${nomeResp} · ${emailResp}`
                          : nomeResp || emailResp || "";
                      const dataFmt =
                        dataEntrega && !Number.isNaN(dataEntrega.getTime())
                          ? format(dataEntrega, "dd/MM/yyyy", { locale: ptBR })
                          : "—";
                      let badgeVariant = "secondary";
                      let badgeText = "";
                      if (diasRestantes < 0) {
                        badgeVariant = "destructive";
                        badgeText = `Vencida (${Math.abs(diasRestantes)} dia${Math.abs(diasRestantes) !== 1 ? "s" : ""})`;
                      } else if (diasRestantes === 0) {
                        badgeText = "Vence hoje";
                      } else {
                        badgeText = `Em ${diasRestantes} dia${diasRestantes !== 1 ? "s" : ""}`;
                      }
                      return (
                        <li
                          key={acao.id ?? `${grupo.editalId}-${idx}`}
                          className="flex flex-wrap items-start gap-2 sm:gap-3 text-sm text-slate-800 border-t border-amber-100/80 first:border-t-0 first:pt-0 pt-2"
                        >
                          <span className="text-slate-500 shrink-0 w-[5.5rem] tabular-nums">{dataFmt}</span>
                          <div className="flex-1 min-w-0">
                            <p className="break-words font-medium text-slate-900">{desc}</p>
                            {textoResponsavel ? (
                              <p className="mt-0.5 flex items-start gap-1.5 text-xs text-slate-600">
                                <User className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" aria-hidden />
                                <span>
                                  <span className="font-medium text-slate-700">Responsável:</span>{" "}
                                  {textoResponsavel}
                                </span>
                              </p>
                            ) : null}
                          </div>
                          <Badge
                            variant={badgeVariant === "destructive" ? "destructive" : "outline"}
                            className={
                              badgeVariant !== "destructive"
                                ? "border-amber-300 text-amber-900 bg-amber-50 shrink-0"
                                : "shrink-0"
                            }
                          >
                            {badgeText}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}